import React, { Fragment } from 'react'
import styled from '~/app/components/styled'
import DomesticAccommodationGroup from '~/app/components/Cart/CartGroup/DomesticAccommodationGroup'
import DomesticLeisureGroup from '~/app/components/Cart/CartGroup/DomesticLeisureGroup'
import DomesticTrainGroup from '~/app/components/Cart/CartGroup/DomesticTrainGroup'
import CartEmpty from '~/app/components/Cart/CartEmpty/CartEmpty'
import { CategoryGroup } from '~/app/components/Cart/CartMisc/CartTabs'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import DomesticBusGroup from '~/app/components/Cart/CartGroup/DomesticBusGroup'
import { useServerCurrentAtQuery } from '~/__generated__/app/query/server-current-at.graphql'
import { getNowSeoulDateTime } from '~/app/utils/datetime'

/**
 * CartGroups component
 * author: jaegeunlee
 * description
 */
const GroupWrapper = styled.div`
  margin-top: 12px;
  padding: 12px 20px 20px 20px;
  background-color: ${({ theme }) => theme.color.white};
  border-bottom: solid 1px ${({ theme }) => theme.color.gray200};
`

interface CartGroupsProps {
  tab: CategoryGroup
  isTrainPackageDiscountAvailable: boolean
  items: CartItemsV3Query['cartItemsV3']
  onCheck?: (checked: boolean, id: string) => void
  onRentTimeClick?: (id: string) => void
  onQuantityChange?: (quantity: number, id: string) => void
  onClose?: (id: string) => void
  onTitleClick?: (id: string) => void
  onHomeClick?: () => void
  onAccommodationSearchClick?: () => void
  onTicketSearchClick?: () => void
  onTransportationSearchClick?: () => void
  onCancelRefreshButtonClick?: () => void
}

const CartGroups: React.FC<CartGroupsProps> = ({
  tab,
  isTrainPackageDiscountAvailable,
  items,
  onCheck,
  onRentTimeClick,
  onQuantityChange,
  onClose,
  onTitleClick,
  onHomeClick,
  onAccommodationSearchClick,
  onTicketSearchClick,
  onTransportationSearchClick,
  onCancelRefreshButtonClick,
}) => {
  const { data: { serverCurrentAt: currentAt } = { serverCurrentAt: getNowSeoulDateTime() } } =
    useServerCurrentAtQuery()
  return (
    <>
      {items.length <= 0 && (
        <CartEmpty
          tab={tab}
          onHomeClick={onHomeClick}
          onAccommodationClick={onAccommodationSearchClick}
          onTicketClick={onTicketSearchClick}
          onTransportationClick={onTransportationSearchClick}
        />
      )}
      {items.length > 0 && (
        <>
          {items.map((item, index) => {
            return (
              <Fragment key={`${item.type}-${index}`}>
                {item.__typename === 'DomesticAccommodationCartItem' && (
                  <GroupWrapper>
                    <DomesticAccommodationGroup
                      propertyName={item.propertyName}
                      currentAt={currentAt}
                      category={item.property.data.category}
                      photo={
                        item.property.data.photos.find(v => v.category === 'REPRESENTATION')?.url
                      }
                      config={item.property?.config}
                      address={item.property.data?.address}
                      options={item.options}
                      onRentTimeClick={onRentTimeClick}
                      onCheck={onCheck}
                      onClose={onClose}
                      onTitleClick={onTitleClick}
                      onCancelRefreshButtonClick={onCancelRefreshButtonClick}
                    />
                  </GroupWrapper>
                )}
                {item.__typename === 'DomesticLeisureCartItem' && (
                  <GroupWrapper>
                    <DomesticLeisureGroup
                      type={item.type}
                      currentAt={currentAt}
                      dealName={item.dealName}
                      options={item.options}
                      onCheck={onCheck}
                      onChange={onQuantityChange}
                      onClose={onClose}
                      onTitleClick={onTitleClick}
                    />
                  </GroupWrapper>
                )}
                {item.__typename === 'DomesticTrainCartItem' && (
                  <GroupWrapper>
                    <DomesticTrainGroup
                      representativeName={item.representativeName}
                      options={item.options}
                      isTrainPackageDiscountAvailable={isTrainPackageDiscountAvailable}
                      onCheck={onCheck}
                      onClose={onClose}
                      onTitleClick={onTitleClick}
                      onCancelRefreshButtonClick={onCancelRefreshButtonClick}
                    />
                  </GroupWrapper>
                )}
                {item.__typename === 'DomesticBusCartItem' && (
                  <GroupWrapper>
                    <DomesticBusGroup
                      busCartItem={item}
                      onTitleClick={onTitleClick}
                      onClose={onClose}
                      onCheck={onCheck}
                      onCancelRefreshButtonClick={onCancelRefreshButtonClick}
                    />
                  </GroupWrapper>
                )}
              </Fragment>
            )
          })}
        </>
      )}
    </>
  )
}

export default CartGroups
